import { Component, inject, input, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AutocompleteModule } from '../../../shared/ui-components/autocomplete/autocomplete.module';
import { IconModule } from '../../../shared/ui-components/icon/icon.module';
import { AutocompleteComponent } from '../../../shared/ui-components/autocomplete/components/autocomplete/autocomplete.component';
import { User } from '../../../libs/models/user/user.model';
import { distinctUntilChanged, filter, shareReplay, switchMap, tap } from 'rxjs';
import { FirestoreDocument } from '../../../libs/shared/models/firestore-document';
import { getOrganizations } from '../../../libs/firestore/feature-utils/organization.firestore.utils';
import { AsyncPipe } from '@angular/common';
import { FilterByPipe } from '../../../shared/pipes/filter-by.pipe';
import { getCandidates } from '../../../libs/firestore/feature-utils/candidate.firestore.utils';
import { CandidatePipe } from '../../../features/candidate/pipes/candidate.pipe';
import { getOrganizationRef } from '../../../libs/firestore/refs/organization.refs';
import { getJobPostings } from '../../../libs/firestore/feature-utils/job-posting.firestore.utils';
import { getJobApplications } from '../../../libs/firestore/feature-utils/job-application.firestore.utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserRole } from '../../../libs/models/user/user-role.enum';
import { getCandidateRef } from '../../../libs/firestore/refs/candidate.refs';
import { distinctUntilRefChanged } from '../../../shared/utils/rxjs.utils';
import { Firestore } from '@angular/fire/firestore';
import { logInfo } from '../../logger/utils/logger.utils';

@Component({
  selector: 'app-main-navigation-search',
  imports: [AsyncPipe, RouterLink, AutocompleteModule, IconModule, FilterByPipe, CandidatePipe],
  templateUrl: './main-navigation-search.component.html',
})
export class MainNavigationSearchComponent {
  // DI
  private readonly firestore = inject(Firestore);

  // Input
  readonly currentUser = input.required<FirestoreDocument<User> | null>();
  readonly organizationId = input.required<string | null>();

  // Streams
  readonly currentUser$ = toObservable(this.currentUser).pipe(
    filter(Boolean),
    distinctUntilRefChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly organizationId$ = toObservable(this.organizationId).pipe(
    filter(Boolean),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly organizations$ = this.currentUser$.pipe(
    filter(Boolean),
    filter((user) => user.data.role === UserRole.ADMIN),
    switchMap((_) => getOrganizations(this.firestore, { orderBy: { field: 'name' } })),
    tap((res) => logInfo(res)) // TODO remove
  );
  readonly candidates$ = this.currentUser$.pipe(
    filter((user) => user?.data.role === UserRole.ADMIN),
    switchMap((_) => getCandidates(this.firestore, { orderBy: { field: 'firstName' } })),
    tap((res) => logInfo(res)) // TODO remove
  );
  readonly organizationJobPostings$ = this.organizationId$.pipe(
    filter(Boolean),
    switchMap((organizationId) => {
      const organizationRef = getOrganizationRef(this.firestore, organizationId);
      return getJobPostings(organizationRef, {
        orderBy: { field: 'createdAt', direction: 'desc' },
      });
    }),
    tap((res) => logInfo(res)) // TODO remove
  );
  readonly organizationJobApplications$ = this.organizationId$.pipe(
    switchMap((organizationId) => {
      const organizationRef = getOrganizationRef(this.firestore, organizationId);
      return getJobApplications(this.firestore, {
        where: { field: 'organizationRef', op: '==', value: organizationRef },
      });
    }),
    tap((res) => logInfo(res)) // TODO remove
  );
  readonly candidateJobApplications$ = this.currentUser$.pipe(
    filter(Boolean),
    filter((user) => user.data.role === UserRole.CANDIDATE),
    switchMap((user) => {
      const candidateRef = getCandidateRef(this.firestore, user.ref.id);
      return getJobApplications(this.firestore, {
        where: { field: 'candidateRef', op: '==', value: candidateRef },
      });
    }),
    tap((res) => logInfo(res)) // TODO remove
  );

  // Views
  @ViewChild(AutocompleteComponent, { static: true }) autocomplete!: AutocompleteComponent;
}
